import * as React from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";

import utils from "../../../../business/helper/Utils";

const cn = utils.cn;

const DropdownMenuRoot = DropdownMenuPrimitive.Root;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuTrigger = ({
  className,
  classes,
  icon,
  disabled = false,
  children,
  ref,
  ...props
}) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={cn(
      "w-fit focus:outline-none flex items-center justify-between",
      className
    )}
    {...props}
    disabled={disabled}
  >
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="5" r="2" fill="#1186E7" />
      <circle cx="12" cy="12" r="2" fill="#1186E7" />
      <circle cx="12" cy="19" r="2" fill="#1186E7" />
    </svg>
  </DropdownMenuPrimitive.Trigger>
);

const DropdownMenuPrimitiveTrigger = ({
  className,
  children,
  ref,
  ...props
}) => (
  <DropdownMenuPrimitive.Trigger
    ref={ref}
    className={cn("focus:outline-none", className)}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Trigger>
);

DropdownMenuTrigger.displayName = DropdownMenuPrimitive.Trigger.displayName;

const DropdownMenuContent = ({
  className,
  children,
  withPortal = true,
  ref,
  ...props
}) => {
  const content = React.useCallback(
    () => (
      <DropdownMenuPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-[91] min-w-[245px] max-w-full rounded-[4px] bg-neutral-100 shadow-popover",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "rounded-lg shadow-select z-30 bg-white text-dimgray-200 cursor-pointer",
          className
        )}
        align="center"
        {...props}
      >
        {children}
      </DropdownMenuPrimitive.Content>
    ),
    [children, className, props, ref]
  );

  if (withPortal) {
    return (
      <DropdownMenuPrimitive.Portal>{content()}</DropdownMenuPrimitive.Portal>
    );
  }
  return content();
};

DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuPrimitiveItem = ({ className, children, ref, ...props }) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "px-4 py-2 w-full leading-6 hover:bg-whitesmoke-400 first:rounded-t-lg last:rounded-b-lg border-b last:border-none border-whitesmoke-100 flex items-center justify-start gap-1 h-12 focus:outline-none",
      className
    )}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Item>
);

DropdownMenuPrimitiveItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = ({ className, children, ref, ...props }) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    style={{
      width: "calc(var(--radix-dropdown-menu-trigger-width) - 8px)",
      maxWidth: "var(--radix-dropdown-menu-content-available-width)",
    }}
    className={cn(
      "relative w-full flex items-center py-1 px-3 text-sl rounded-[4px] cursor-default select-none outline-none rtl:flex-row-reverse",
      "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className
    )}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
);

DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

export const DropdownMenu = Object.assign(DropdownMenuRoot, {
  Group: DropdownMenuGroup,
  Trigger: DropdownMenuTrigger,
  PrimitiveTrigger: DropdownMenuPrimitiveTrigger,
  Content: DropdownMenuContent,
  Item: DropdownMenuPrimitive.Item,
  PrimitiveItem: DropdownMenuPrimitiveItem,
  ItemIndicator: DropdownMenuPrimitive.ItemIndicator,
  CheckboxItem: DropdownMenuCheckboxItem,
});
